import { useMutation, useQueryClient } from 'react-query';

import type { IStandardErrorResponse } from 'src/api/common';
import { useNotifications } from 'src/notifications';
import { queryKeys } from 'src/services/api/constants';
import { apiClient } from 'src/services/api/client';
import { getApiErrorMessage } from 'src/services/api/utils';
import { GET_PROPERTIES_PATH } from 'src/services/api/properties/constants';
import type {
    IProperty,
    IUpdatePropertyResponse
} from 'src/services/api/properties/types';

/**
 * Update only the notes of a property.
 * Unlike useUpdateProperty, merges the new notes into existing cache entries
 * (preserving fetched facts/ancestors) instead of replacing them.
 */
export const useUpdatePropertyNotes = (propertyId: number) => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<IProperty, IStandardErrorResponse, string>(
        async (notes) => {
            const path = `${GET_PROPERTIES_PATH}/${propertyId}`;
            const res = await apiClient(path, {
                method: 'patch',
                json: { property: { notes } },
            }).json<IUpdatePropertyResponse>();

            return res.property;
        },
        {
            onSuccess: (data) => {
                queryClient.setQueryData<IProperty[]>([queryKeys.properties], (oldData) => {
                    if (!oldData) {
                        return [];
                    }

                    return oldData.map((property) => {
                        return property.id === data.id ? { ...property, notes: data.notes } : property;
                    });
                });

                queryClient.setQueriesData<IProperty | undefined>(
                    [queryKeys.properties, data.id],
                    (oldData) => {
                        return oldData ? { ...oldData, notes: data.notes } : oldData;
                    },
                );

                addNotification('Property notes updated', 'success');
            },
            onError: (error) => {
                const errorMessage = getApiErrorMessage(error) || 'Failed to update property notes';
                addNotification(errorMessage, 'error');
            },
        },
    );
};
